import { postJson } from "./http";
import type { ZohoCurrencyDTO } from "./zohoRecordsApi";

/**
 * Currencies — the currency screen's catalog of what Zoho supports (read live)
 * and its one write, switching a currency on in a client's real Zoho Books.
 */

/**
 * One of the 176 currencies Zoho will let an organisation switch on, with the
 * defaults it would fill the form with.
 *
 * These come from Zoho's `meta/currencies`, not from the client's books — the
 * currencies a client already has are `ZohoCurrencyDTO`, which carry an id and
 * an exchange rate. A row here is a currency that could exist.
 */
export interface SupportedCurrencyDTO {
  code: string;
  currency_name: string;
  currency_symbol: string;
  /** Zoho's own sample, e.g. "12,34,567.89" for INR. */
  currency_format: string;
  price_precision: number;
}

export interface SupportedCurrenciesDTO {
  currencies: SupportedCurrencyDTO[];
  /**
   * Which formats are legal at each precision, keyed "0", "2", "3".
   *
   * The pairing is the point: a 2-decimal format on a 0-decimal currency is
   * something Zoho's own dialog cannot produce, because choosing the decimal
   * places is what fills the format list. Ours does the same.
   */
  formats: Record<string, string[]>;
}

interface SupportedCurrenciesEnvelope {
  success: boolean;
  data: SupportedCurrenciesDTO;
}

/**
 * Every currency Zoho supports, for this client's organisation.
 *
 * Read live rather than shipped in the app, for the same reason as the tax
 * sections: the list is Zoho's, it is closed, and the per-currency defaults
 * carry facts that are easy to get wrong by hand — JPY has no decimal places,
 * KWD has three, INR groups as 12,34,567.89.
 *
 * Reads only. Nothing is written to the books by this call.
 */
export const fetchSupportedCurrencies = async (
  publicId: string
): Promise<SupportedCurrenciesDTO> =>
  (await postJson<SupportedCurrenciesEnvelope>("/api/onboarding/currencies/supported", {
    public_id: publicId,
  })).data;

/**
 * What the add-currency form collects. Only the code is required — everything
 * else falls back to Zoho's own default for that currency.
 */
export interface NewCurrency {
  currency_code: string;
  currency_symbol?: string;
  currency_name?: string;
  price_precision?: number;
  currency_format?: string;
}

export interface CreatedCurrencyDTO {
  created: { currency_id: string; currency_code: string; currency_name: string };
  /**
   * Set when the currency was added but the re-read that follows it failed. The
   * currency exists; the list beside it may be a moment behind.
   */
  reread_error: string | null;
  /** The whole currency list again, re-read from Zoho after the create. */
  currencies: ZohoCurrencyDTO[];
}

interface CreatedCurrencyEnvelope {
  success: boolean;
  data: CreatedCurrencyDTO;
}

/**
 * Switch a currency on in the client's real Zoho Books.
 *
 * THIS WRITES TO A CUSTOMER'S BOOKS. Do not call it speculatively or retry it
 * blind; a failure that reached Zoho may still have added the currency.
 *
 * THERE IS NO OFF SWITCH TO UNDO IT WITH. A currency is not deactivated the way
 * a tax rate is — the row Zoho returns carries no active flag, and its own
 * screen offers only Edit and Delete. So undoing this means deleting the
 * currency, which is why the tick list on the screen does not write: unticking
 * records that the client does not use a currency, which is a different thing
 * from asking for it to be removed from their books.
 *
 * The readable refusal lives under `errors.currency` — "This client's books
 * already have AED…", or Zoho's own refusal passed through.
 */
export const createCurrency = async (
  publicId: string,
  currency: NewCurrency
): Promise<CreatedCurrencyDTO> =>
  (
    await postJson<CreatedCurrencyEnvelope>(
      "/api/onboarding/currencies/create",
      { public_id: publicId, ...currency },
      ["currency"]
    )
  ).data;
